import { useTheme } from './useTheme'

/* ============================================
   Types
============================================ */

export interface IControlColors {
  onBgColor: string
  onTextColor: string
  onTextBgColor: string
  onBorderColor: string

  offBgColor: string
  offTextColor: string
  offTextBgColor: string
  offBorderColor: string

  unknownBgColor: string
  unknownTextColor: string
  unknownTextBgColor: string
  unknownBorderColor: string
}

export interface IControlSimpleColors {
  bgColor: string
  textColor: string
  textBgColor: string
  borderColor: string
}

export interface IAllControlColors {
  alarmControlColors: IControlColors
  stateControlColors: IControlColors
  genericControlColors: IControlSimpleColors
}

export type ControlNotificationIconType = 'arm' | 'disarm' | 'locked' | 'unlocked' | 'on' | 'off' | 'info'

/* ============================================
   Shared helpers (spinner-style borders)
   - only left/right borders visible
   - top/bottom transparent
============================================ */

const spinnerBorder = (left: string, right: string) => `${left} ${right} border-t-transparent border-b-transparent`

/* ============================================
   DARK MODE
============================================ */
/**
 * Dark mode: control fills are dark/saturated.
 * Borders should be lighter so they read on:
 * - the control fill
 * - darker surfaces behind it
 */

const AlarmControlDarkColors: IControlColors = {
  onBgColor: 'bg-red-800',
  onTextColor: 'text-red-100',
  onTextBgColor: 'bg-red-900/40',
  onBorderColor: spinnerBorder('border-l-red-200/70', 'border-r-red-200/70'),

  offBgColor: 'bg-emerald-800',
  offTextColor: 'text-emerald-100',
  offTextBgColor: 'bg-emerald-900/40',
  offBorderColor: spinnerBorder('border-l-emerald-200/70', 'border-r-emerald-200/70'),

  unknownBgColor: 'bg-sky-800',
  unknownTextColor: 'text-sky-100',
  unknownTextBgColor: 'bg-sky-900/40',
  unknownBorderColor: spinnerBorder('border-l-sky-200/70', 'border-r-sky-200/70')
}

const StateControlDarkColors: IControlColors = {
  onBgColor: 'bg-emerald-700',
  onTextColor: 'text-emerald-100',
  onTextBgColor: 'bg-emerald-900/40',
  onBorderColor: spinnerBorder('border-l-emerald-200/70', 'border-r-emerald-200/70'),

  // offBgColor: 'bg-slate-700',
  // offTextColor: 'text-slate-200',
  // offTextBgColor: 'bg-slate-900/40',
  // offBorderColor: spinnerBorder('border-l-slate-200', 'border-r-slate-200'),

  offBgColor: 'bg-red-800',
  offTextColor: 'text-red-100',
  offTextBgColor: 'bg-red-900/40',
  offBorderColor: spinnerBorder('border-l-red-200/70', 'border-r-red-200/70'),

  unknownBgColor: 'bg-sky-700',
  unknownTextColor: 'text-sky-100',
  unknownTextBgColor: 'bg-sky-900/40',
  unknownBorderColor: spinnerBorder('border-l-sky-200/70', 'border-r-sky-200/70')
}

const GenericControlDarkColors: IControlSimpleColors = {
  bgColor: 'bg-slate-700',
  textColor: 'text-slate-200',
  textBgColor: 'bg-slate-900/40',
  borderColor: spinnerBorder('border-l-slate-200/70', 'border-r-slate-200/70')
}

/* ============================================
   LIGHT MODE
============================================ */
/**
 * Light mode: control fills are mid/light.
 * Borders should be darker so they read on:
 * - light surfaces behind it
 * - the control fill itself
 */

const AlarmControlLightColors: IControlColors = {
  onBgColor: 'bg-red-600',
  onTextColor: 'text-white',
  onTextBgColor: 'bg-red-700/30',
  onBorderColor: spinnerBorder('border-l-red-100/70', 'border-r-red-100/70'),

  offBgColor: 'bg-emerald-600',
  offTextColor: 'text-white',
  offTextBgColor: 'bg-emerald-700/30',
  offBorderColor: spinnerBorder('border-l-emerald-100/70', 'border-r-emerald-100/70'),

  unknownBgColor: 'bg-sky-600',
  unknownTextColor: 'text-white',
  unknownTextBgColor: 'bg-sky-700/30',
  unknownBorderColor: spinnerBorder('border-l-sky-100/70', 'border-r-sky-100/70')
}

const StateControlLightColors: IControlColors = {
  onBgColor: 'bg-emerald-500',
  onTextColor: 'text-white',
  onTextBgColor: 'bg-emerald-600/30',
  onBorderColor: spinnerBorder('border-l-emerald-100/70', 'border-r-emerald-100/70'),

  // offBgColor: 'bg-slate-300',
  // offTextColor: 'text-slate-800',
  // offTextBgColor: 'bg-slate-400/40',
  // offBorderColor: spinnerBorder('border-l-slate-100/70', 'border-r-slate-100/70'),

  offBgColor: 'bg-red-600',
  offTextColor: 'text-white',
  offTextBgColor: 'bg-red-700/30',
  offBorderColor: spinnerBorder('border-l-red-100/70', 'border-r-red-100/70'),

  unknownBgColor: 'bg-sky-500',
  unknownTextColor: 'text-white',
  unknownTextBgColor: 'bg-sky-600/30',
  unknownBorderColor: spinnerBorder('border-l-sky-100/70', 'border-r-sky-100/70')
}

const GenericControlLightColors: IControlSimpleColors = {
  bgColor: 'bg-slate-200',
  textColor: 'text-slate-800',
  textBgColor: 'bg-slate-300/40',
  borderColor: spinnerBorder('border-l-slate-600/70', 'border-r-slate-600/70')
}

const getControlColorsForMode = (dark: boolean): IAllControlColors =>
  dark
    ? {
        alarmControlColors: AlarmControlDarkColors,
        stateControlColors: StateControlDarkColors,
        genericControlColors: GenericControlDarkColors
      }
    : {
        alarmControlColors: AlarmControlLightColors,
        stateControlColors: StateControlLightColors,
        genericControlColors: GenericControlLightColors
      }

const getResolvedControlThemeMode = (): 'light' | 'dark' => {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark'
  }

  return 'light'
}

export const getControlNotificationWrapperClasses = (type: ControlNotificationIconType): string => {
  const dark = getResolvedControlThemeMode() === 'dark'
  const { alarmControlColors, stateControlColors } = getControlColorsForMode(dark)

  if (['arm', 'locked'].includes(type)) {
    return `${alarmControlColors.onBgColor} ${alarmControlColors.onTextColor} border border-current rounded-[8px] p-[3px]`
  }

  if (['disarm', 'unlocked'].includes(type)) {
    return `${alarmControlColors.offBgColor} ${alarmControlColors.offTextColor} border border-current rounded-[8px] p-[3px]`
  }

  if (type === 'on') {
    return `${stateControlColors.onBgColor} ${stateControlColors.onTextColor} border border-current rounded-[8px] p-[3px]`
  }

  if (type === 'off') {
    return `${stateControlColors.offBgColor} ${stateControlColors.offTextColor} border border-current rounded-[8px] p-[3px]`
  }

  return `${stateControlColors.unknownBgColor} ${stateControlColors.unknownTextColor} border border-current rounded-[8px] p-[3px]`
}

/* ============================================
   Hook
============================================ */

export const useControlColors = (): IAllControlColors => {
  const { globalThemeMode } = useTheme()

  const dark = globalThemeMode === 'dark'

  return getControlColorsForMode(dark)
}
